import { Dialog } from "@headlessui/react";
import { ModalFoterBox, ModalHeaderBox } from "../../components/box/Box";
import Button from "../../components/button/Button";
import { Modal, ModalProp } from "../../components/modal/Modal";
import { forwardRef, useState, useEffect, useImperativeHandle } from "react";
import { useForm } from "react-hook-form";
import TableList, { GridRow } from "../../components/table/TableList";
import { auth } from "../../firebase";
import pointDataQuery from "../../firestore/PointData.query";
import { PointTypeId } from "../../config/const";

export type MoviePointSettingModalProp = {
  point?: PointForm;
  pointId?: string;
  onSubmit: (data: PointForm) => void;
  onSelectCancel?: () => void;
}

export interface PointForm {
  id: string,
  categoryId: number;
  categoryName: string;
  typeId: string;
  typeName: string;
  pointName: string;
  point: number;
}

const MoviePointSettingModal = forwardRef(({
  open,
  toggle,
  closeOnClickOutside,
  point,
  pointId,
  onSubmit,
}: ModalProp & MoviePointSettingModalProp, ref) => {
  const col = [
    { field: "radio", headerName: '', style: "w-max-12", },
    { field: "pointName", headerName: "ポイント名", style: "w-1/3", },
    { field: "typeName", headerName: "ポイントタイプ", style: "w-1/3", },
    { field: "point", headerName: "ポイント数", style: "w-1/6 text-right", },
  ];

  const { handleSubmit, reset } = useForm<PointForm>({
    defaultValues: {
      ...point,
    },
  });

  const [selectedToken, setSelectedToken] = useState<string | null>(null);
  const [initialSelectedToken, setInitialSelectedToken] = useState<string | null>(null);
  const [filteredData, setFilteredData] = useState<PointForm[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      const currentUser = auth.currentUser;
      const data = await pointDataQuery.getPtAwardedSettingsByTypeId(currentUser?.tenantId!, PointTypeId.VIEW_MOVIE);
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const transformedData = data
        .filter(item => {
          const from = item.validPeriodDateFrom ? new Date(item.validPeriodDateFrom) : null;
          const to = item.validPeriodDateTo ? new Date(item.validPeriodDateTo) : null;
          // 開始日が未来なら除外
          if (from) {
            const fromDate = new Date(from.getFullYear(), from.getMonth(), from.getDate());
            if (today < fromDate) {
              return false;
            }
          }
          // 終了日が過去なら除外
          if (to) {
            const toDate = new Date(to.getFullYear(), to.getMonth(), to.getDate());
            if (today > toDate) {
              return false;
            }
          }
          return true;
        })
        .map(item => ({
          id: item.id,
          categoryId: item.categoryId,
          categoryName: item.categoryName,
          typeId: item.typeId,
          typeName: item.typeName,
          pointName: item.name,
          point: item.basePoint!,
        }))
        .sort((a, b) => a.pointName.localeCompare(b.pointName)); // nameの昇順
      setFilteredData(transformedData);
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (open && pointId) {
      setSelectedToken(pointId);
    }
  }, [open, pointId]);

  const row: GridRow[] = filteredData.map((item) => ({
    rowData: {
      radio: (
        <input
          id={`${item.id}`}
          name="pointSelection"
          type="radio"
          onChange={() => handleSelectToken(item.id)}
          checked={selectedToken === item.id}
        />
      ),
      typeName: item.typeName,
      pointName: item.pointName,
      point: (
        <div className="text-right">
          {item.point}
        </div>
      ),
    }
  }));

  const handleCancel = () => {
    setSelectedToken(initialSelectedToken);
    toggle();
  };

  const handleSelectToken = (id: string) => {
    setSelectedToken(id);
  };

  useImperativeHandle(ref, () => ({
    resetForm: () => {
      setSelectedToken(null);
      reset({
        id: "",
        categoryId: 0,
        categoryName: "",
        typeId: "",
        typeName: "",
        pointName: "",
        point: 0
      });
    }
  }));

  const handlePointSettingSubmit = () => {
    const selectedData = filteredData.find(item => selectedToken === item.id);
    if (selectedData) {
      onSubmit(selectedData);
    }
    toggle();
  };

  return (
    <Modal
      open={open}
      toggle={toggle}
      closeOnClickOutside={closeOnClickOutside}
    >
      <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:w-full max-w-screen-md">
        <div className="px-2 pb-4 sm:p-4">
          <div className="text-center mt-3 mb-4">
            <ModalHeaderBox title={"ポイント設定選択"} />
          </div>
          <div className={"max-h-[calc(100vh_-_20rem)] overflow-auto px-4 pb-4 pt-1"}>
            <TableList
              gridCol={col}
              gridRow={row}
              sort={false}
              footer={false}
            />
          </div>
        </div>
        <ModalFoterBox>
          <Button
            className="mx-2 mt-1 w-[132px]"
            outline
            onClick={handleCancel}
            children={"キャンセル"}
          />
          <Button
            className="mt-1 mr-2 w-[132px]"
            color="indigo"
            onClick={handleSubmit(handlePointSettingSubmit)}
            children={"選択"}
          />
        </ModalFoterBox>
      </Dialog.Panel>
    </Modal>
  );
});

MoviePointSettingModal.displayName = "MoviePointSettingModal";
export default MoviePointSettingModal;